/**
 * PensionSimulatorV2 - Projection chart with confidence bands.
 * Based on Lovable design, using chirigota calculation logic and API connections.
 */
import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { MetricCard } from 'components/ui/metric-card';
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer
} from 'recharts';
import { TrendingUp, Building2, User, Info, Lock, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';
import { ArcaContext } from '../../context/ArcaContext.context';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import useArcaPlanReturns from '../../hooks/useArcaPlanReturns';
import useOwnPlanReturn from '../../hooks/useOwnPlanReturn';
import getCurrentUser from '../../api/users/getCurrentUser';
import getCompanyInfoEppConfig from '../../api/companies/getCompanyInfoEppConfig';
import getAdhesionDetails, { AdhesionMode } from '../../api/users/getAdhesionDetails';
import { getMyPensionPlanTransfers } from '../../api/pension-plan-transfers';
import { CurrentUserI } from '../../api/users/interfaces/Users';
import { EmployerContributionTypeEnum } from '../../types/enums';
import { formatArcaNumber, fmtNum } from '../../utils/formatArcaNumbers';
import {
	calculatePensionPlanMetrics,
	UserType,
	PlanTypology
} from '../../utils/pensionPlanCalculations';
import { resolveActiveArcaPlan } from '../../utils/resolveActiveArcaPlan';
import { esExEmpleado } from '../../utils/esExEmpleado';
import {
	projectByYear,
	shiftAnnualPct,
	yearsToRetirementFromAge
} from '../../utils/monthlyProjection';
import { periodRangeLabel, rateOf, todayPct } from '../../utils/transferReturns';
import { employmentPlanCatalogSlug } from '../../constants/employmentPlanDocs';
import { periodLabel, periodWithRange } from '../MiBeneficioV2/projectionLegend';

interface PensionSimulatorV2Props {
	/** Su edad, solo si la sabemos: sin ella no se piden los tramos del ciclo de vida. */
	currentAge?: number;
	/**
	 * Slug del catálogo de su plan elegido (`GET /users/plans`). Manda sobre el del traspaso, que
	 * puede ser otro plan o uno que ya no se ofrece (chirigota#598). El empleado B2B sigue con el
	 * plan de empleo de su empresa.
	 */
	planCatalogSlug?: string | null;
	/** `GET /users/plans` aún no ha llegado: «cargando» en vez de la rentabilidad del traspaso. */
	plansLoading?: boolean;
}

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.58, 1] as const }
	})
};

const PensionSimulatorV2: React.FC<PensionSimulatorV2Props> = ({
	currentAge,
	planCatalogSlug,
	plansLoading = false
}) => {
	const { token, companyId } = useContext(ArcaContext);
	const {
		state: { translate }
	} = useContext(I18nContext);

	// States for data
	// Plan cuya rentabilidad se proyecta (nombre en el catálogo de comparsa): su plan de Arca
	// (autónomo/individual) o el plan de empleo de su empresa (B2B). `null` si no hay ninguno.
	const [planSlug, setPlanSlug] = useState<string | null>(null);
	// Empleado B2B: proyecta el plan de empleo de su empresa (no su plan elegido).
	const [usesEmploymentPlan, setUsesEmploymentPlan] = useState(false);
	// Rentabilidad de cada plan de Arca, de comparsa (`/plan-returns/arca-plans`, chirigota#576).
	// Con su edad, los de ciclo de vida llegan con sus tramos (chirigota#592).
	const arcaPlanReturns = useArcaPlanReturns(token, yearsToRetirementFromAge(currentAge));
	// Saldo acumulado actual del usuario (igual que Inicio). El simulador proyecta desde aquí.
	const [totalSaved, setTotalSaved] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(true);
	// El autónomo NO tiene aportación de empresa (PROMOTER_CONTRIBUTION_NOT_AVAILABLE_FOR_FREELANCE),
	// y el ex-empleado ya no la recibe (product#87): se ocultan los bloques de empresa y la
	// aportación de empresa cuenta como 0.
	const [sinAportacionEmpresa, setSinAportacionEmpresa] = useState(false);

	// Editable states
	const [employeeMonthly, setEmployeeMonthly] = useState<number>(0);
	const [minContribution, setMinContribution] = useState<number>(0);
	const [companyMonthly, setCompanyMonthly] = useState<number>(0);

	// Age-derived values
	const effectiveAge = currentAge && currentAge > 0 ? currentAge : 30;
	const retirementAge = 67;
	const yearsToRetirement = Math.max(0, retirementAge - effectiveAge);

	// Fetch initial data
	useEffect(() => {
		const fetchData = async () => {
			try {
				// La config de empresa NO va en el Promise.all: para autónomos el backend
				// responde 500 ("no accessible companies") y tumbaría el resto del fetch.
				const [userResponse, transfersResponse] = await Promise.all([
					getCurrentUser(token),
					getMyPensionPlanTransfers(token)
				]);

				const userData = userResponse.data as CurrentUserI;
				const freelance = !!userData?.user?.isFreelance;
				// Ex-empleado (product#87): su plan de empleo sigue, pero ni la empresa ni la nómina
				// le aportan ya. Se proyecta su plan de empleo desde su saldo. Solo en la vista de
				// empleo: si además es autónomo, su plan de autónomo no cambia (§4.2).
				const exEmpleado = !freelance && esExEmpleado(userData);
				setSinAportacionEmpresa(freelance || exEmpleado);

				const transfers =
					transfersResponse?.success && transfersResponse?.data ? transfersResponse.data : [];

				// Su plan de Arca (autónomo/individual). El empleado B2B lo cambia más abajo por el
				// plan de empleo de su empresa.
				setPlanSlug(resolveActiveArcaPlan(transfers)?.catalogSlug ?? null);

				// Saldo acumulado: misma fuente que Inicio (adhesión + métricas), con el
				// mode/typology del tipo de usuario para no contaminar planes mixtos.
				const adhesionMode: AdhesionMode = freelance
					? 'FREELANCE'
					: userData?.user?.isIndividual
						? 'INDIVIDUAL'
						: 'EMPLOYMENT';
				const planTypology: PlanTypology = freelance
					? 'AUTONOMOS'
					: userData?.user?.isIndividual
						? 'INDIVIDUAL'
						: 'EMPLEO';
				try {
					const adhesionRes = await getAdhesionDetails(token, adhesionMode);
					const adhesionDetails = adhesionRes?.data ?? [];
					const metrics = calculatePensionPlanMetrics({
						adhesionDetails,
						userType: UserType.INDIVIDUAL,
						transfers,
						planTypology
					});
					setTotalSaved(metrics.totalSaved || 0);
				} catch (adhesionError) {
					console.error('Error fetching adhesion details:', adhesionError);
				}

				// Aportación del partícipe: SIEMPRE desde su user_epp_config (no depende de empresa),
				// con el MISMO fallback que Inicio (0 si no está configurada) para que coincidan.
				// El ex-empleado ya no aporta por nómina: parte de 0 (y puede simular con el slider).
				const userContrib = exEmpleado
					? 0
					: Number(userData?.userEppConfig?.data?.pensionContribution) || 0;

				if (freelance) {
					// Autónomo: sin aportación de empresa, sin mínimo de empresa.
					setCompanyMonthly(0);
					setMinContribution(0);
					setEmployeeMonthly(userContrib);
				} else {
					// Empleado B2B: aportación de empresa + mínimo desde la config de empresa, y la
					// rentabilidad del plan de empleo de la empresa (no la de un plan suyo individual:
					// sin config de empresa, sin datos). El usuario individual conserva su plan.
					const isIndividual = !!userData?.user?.isIndividual;
					const useEmploymentPlan = (slug: string | null) => {
						if (isIndividual) return;
						setPlanSlug(slug);
						setUsesEmploymentPlan(true);
					};
					try {
						const companyResponse = await getCompanyInfoEppConfig(token, companyId);
						const company =
							companyResponse.data?.allCompanies?.find((c) => c.id === companyId) ||
							companyResponse.data?.allCompanies?.[0];
						if (company) {
							const {
								employerContributionType,
								employerContributionAmount,
								employeeContributionMinimum
							} = company.companyEppConfig;

							const companyContrib =
								employerContributionType === EmployerContributionTypeEnum.FIXED_AMOUNT
									? Number(employerContributionAmount)
									: ((Number(employerContributionAmount) / 100) *
											(userData?.user?.grossAnnualSalary ?? 30000)) /
										12;
							setCompanyMonthly(exEmpleado ? 0 : Math.round(companyContrib));

							// La rentabilidad, la del plan de empleo que tiene contratado la empresa.
							useEmploymentPlan(
								employmentPlanCatalogSlug(company.companyEppConfig.caserPlanSelected)
							);

							// El mínimo es de la empresa: al ex-empleado ya no le obliga.
							const min = exEmpleado ? 0 : Number(employeeContributionMinimum) || 0;
							setMinContribution(min);
							setEmployeeMonthly(Math.max(userContrib, min));
						} else {
							useEmploymentPlan(null);
							setEmployeeMonthly(userContrib);
						}
					} catch (companyError) {
						// Si la config de empresa falla, respetamos al menos la aportación del usuario.
						console.error('Error fetching company config:', companyError);
						useEmploymentPlan(null);
						setEmployeeMonthly(userContrib);
					}
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		if (token) {
			fetchData();
		}
	}, [token, companyId]);

	// Rentabilidad de comparsa del plan: `undefined` mientras llega; `null` sin datos (nunca una
	// tasa por defecto: entonces no se proyecta).
	// Si su plan no viene en la lista de planes que ofrecemos (inactivo), se pide aparte.
	// Su plan elegido va fuera del efecto de carga: si llega después, no se repite la carga ni
	// compiten dos cargas por `planSlug`. Hasta acabar la carga no se sabe si manda el plan de
	// empleo, así que no se pide nada (la pantalla ya enseña «cargando»).
	const planReturn = useOwnPlanReturn(
		token,
		plansLoading || isLoading ? undefined : usesEmploymentPlan ? planSlug : planCatalogSlug ?? planSlug,
		yearsToRetirementFromAge(currentAge),
		arcaPlanReturns
	);

	// Proyección desde el SALDO actual, mes a mes (aportación a final de mes) con la tasa mensual
	// neta de comparsa. Bandas favorable/desfavorable: ±10 % sobre esa rentabilidad, como
	// escenarios ilustrativos (con una tasa negativa, la favorable sigue siendo la mejor).
	// Ciclo de vida con edad: la curva sigue sus tramos y las bandas también, cada tramo movido los
	// mismos puntos (el 10 % de la rentabilidad de hoy).
	const totalMonthly = employeeMonthly + companyMonthly;
	const chartData = useMemo(() => {
		if (!planReturn) return [];
		const annual = planReturn.annualPct;
		const band = Math.abs(annual) * 0.1;
		const years = Math.round(yearsToRetirement);
		const rate = rateOf(planReturn);
		const base = projectByYear(totalSaved, totalMonthly, rate, years);
		const fav = projectByYear(totalSaved, totalMonthly, shiftAnnualPct(rate, band), years);
		const desf = projectByYear(totalSaved, totalMonthly, shiftAnnualPct(rate, -band), years);
		return base.map((plan, y) => ({
			age: effectiveAge + y,
			plan: Math.round(plan),
			favorable: Math.round(fav[y]),
			desfavorable: Math.round(desf[y]),
			simple: Math.round(totalSaved + totalMonthly * 12 * y)
		}));
	}, [planReturn, effectiveAge, yearsToRetirement, totalMonthly, totalSaved]);

	// La cifra principal es el último punto de la curva: así coinciden siempre.
	const finalPlan = chartData[chartData.length - 1]?.plan ?? 0;
	const finalSimple = chartData[chartData.length - 1]?.simple ?? 0;
	const totalContributed = totalMonthly * 12 * Math.round(yearsToRetirement);
	const compoundGain = finalPlan - (totalSaved + totalContributed);
	const maxSlider = Math.max(500, employeeMonthly);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
			</div>
		);
	}

	return (
		<motion.div initial="hidden" animate="visible" className="space-y-4 sm:space-y-6">
			{/* Controls */}
			<motion.div custom={0} variants={fadeUp}>
				<div className="surface-card p-4 sm:p-6">
					<div
						className={`grid grid-cols-1 sm:grid-cols-2 ${sinAportacionEmpresa ? '' : 'lg:grid-cols-3'} gap-5 sm:gap-6`}
					>
						{/* Age — read-only */}
						<div>
							<Label className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
								{translate('simulator.pension.yourAge')}
								<Lock className="w-3 h-3 text-muted-foreground/60" />
							</Label>
							<div className="flex items-center gap-3">
								<span className="text-3xl font-bold text-foreground">{effectiveAge}</span>
								<span className="text-sm text-muted-foreground">
									{translate('simulator.pension.ageUnit')}
								</span>
							</div>
							<p className="text-xs text-muted-foreground mt-2">
								{translate('simulator.pension.retirement')}:{' '}
								<span className="font-semibold">{retirementAge}</span> ·{' '}
								{translate('simulator.pension.horizon')}:{' '}
								<span className="font-semibold">{periodLabel(translate, yearsToRetirement)}</span>
							</p>
						</div>

						{/* Company contribution — read-only (no aplica a autónomos) */}
						{!sinAportacionEmpresa && (
							<div>
								<Label className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
									<Building2 className="w-3.5 h-3.5" />
									{translate('simulator.pension.companyContribution')}
									<Lock className="w-3 h-3 text-muted-foreground/60" />
								</Label>
								<div className="flex items-baseline gap-1">
									<span className="text-3xl font-bold text-foreground">
										{formatArcaNumber(companyMonthly)}
									</span>
									<span className="text-sm text-muted-foreground">
										€/{translate('contributions.month')}
									</span>
								</div>
								<p className="text-xs text-muted-foreground mt-2">
									{translate('simulator.pension.companyFixed')}
								</p>
							</div>
						)}

						{/* Employee contribution — editable */}
						<div className={sinAportacionEmpresa ? '' : 'sm:col-span-2 lg:col-span-1'}>
							<Label className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
								<User className="w-3.5 h-3.5" />
								{translate('simulator.pension.yourContribution')}
							</Label>
							<div className="flex items-baseline gap-1 mb-2">
								<span className="text-3xl font-bold text-primary">
									{formatArcaNumber(employeeMonthly)}
								</span>
								<span className="text-sm text-muted-foreground">
									€/{translate('contributions.month')}
								</span>
							</div>
							<Slider
								value={[employeeMonthly]}
								onValueChange={(v) => setEmployeeMonthly(Math.round(v[0] / 10) * 10)}
								min={minContribution}
								max={maxSlider}
								step={10}
								className="slider-arca"
							/>
							<p className="text-xs text-muted-foreground mt-1">
								{translate('simulator.pension.maxLimit')}:{' '}
								<span className="font-semibold">
									{maxSlider} €/{translate('contributions.month')}
								</span>
							</p>
						</div>
					</div>
				</div>
			</motion.div>

			{!planReturn ? (
				<p className="surface-card p-6 text-sm text-muted-foreground text-center">
					{planReturn === undefined
						? translate('simulator.pension.returnLoading')
						: translate('simulator.pension.returnNoData')}
				</p>
			) : (
				<>
					{/* Results summary */}
					<div
						className={`grid grid-cols-1 ${sinAportacionEmpresa ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3 sm:gap-4`}
					>
						<MetricCard
							index={1}
							label={translate('simulator.pension.withPlan')}
							value={`${formatArcaNumber(finalPlan)} €`}
							sub={`${compoundGain >= 0 ? '+' : ''}${formatArcaNumber(compoundGain)} € ${translate('simulator.pension.compoundInterest')}`}
							icon={TrendingUp}
						/>
						<MetricCard
							index={2}
							label={translate('simulator.pension.simpleSavings')}
							value={`${formatArcaNumber(finalSimple)} €`}
							sub={translate('simulator.pension.noReturn')}
							icon={Wallet}
						/>
						{!sinAportacionEmpresa && (
							<MetricCard
								index={3}
								label={translate('simulator.pension.companyTotal')}
								value={`${formatArcaNumber(companyMonthly * 12 * yearsToRetirement)} €`}
								sub={translate('simulator.pension.extraMoney')}
								icon={Building2}
							/>
						)}
					</div>

					{/* Chart with confidence bands */}
					<motion.div custom={4} variants={fadeUp}>
						<div className="surface-card p-3 sm:p-6">
							<div className="h-[280px] sm:h-[380px] min-h-[260px] w-full">
								<ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={260}>
									<AreaChart data={chartData} margin={{ top: 10, right: 16, left: 8, bottom: 18 }}>
										<defs>
											<linearGradient id="bandGradient" x1="0" y1="0" x2="0" y2="1">
												<stop offset="0%" stopColor="#0CA855" stopOpacity={0.15} />
												<stop offset="100%" stopColor="#0CA855" stopOpacity={0.03} />
											</linearGradient>
										</defs>
										<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
										<XAxis
											dataKey="age"
											tick={{ fontSize: 11, fill: '#9ca3af' }}
											axisLine={false}
											tickLine={false}
										/>
										<YAxis
											tick={{ fontSize: 11, fill: '#9ca3af' }}
											tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
											width={50}
											axisLine={false}
											tickLine={false}
										/>
										<Tooltip
											contentStyle={{
												backgroundColor: 'white',
												border: '1px solid #e5e7eb',
												borderRadius: '8px',
												fontSize: '12px'
											}}
											formatter={(value, name) => {
												const labels: Record<string, string> = {
													plan: translate('simulator.pension.baseScenario'),
													favorable: translate('simulator.pension.favorableScenario'),
													desfavorable: translate('simulator.pension.unfavorableScenario')
												};
												return [
													`${formatArcaNumber(value as number)} €`,
													labels[name as string] || name
												];
											}}
											labelFormatter={(label) => `${translate('simulator.pension.age')}: ${label}`}
										/>
										<Legend
											verticalAlign="top"
											align="right"
											wrapperStyle={{ fontSize: '11px', paddingBottom: '12px' }}
											formatter={(value) => {
												const labels: Record<string, string> = {
													favorable: `${translate('simulator.pension.favorableShort')} (+10 % s/ base)`,
													plan: `${translate('simulator.pension.base')} (${todayPct(translate, planReturn, `${fmtNum(planReturn.annualPct, 1)} %`)})`,
													desfavorable: `${translate('simulator.pension.unfavorableShort')} (−10 % s/ base)`
												};
												return labels[value] || value;
											}}
										/>
										{/* Confidence band — favorable (verde) */}
										<Area
											type="monotone"
											dataKey="favorable"
											stroke="#0CA855"
											strokeWidth={1.5}
											strokeDasharray="4 3"
											fill="url(#bandGradient)"
											dot={false}
										/>
										{/* Main projection line — base (gris, referencia) */}
										<Area
											type="monotone"
											dataKey="plan"
											stroke="#6B7280"
											strokeWidth={2.5}
											fill="none"
											dot={false}
										/>
										{/* Confidence band — desfavorable (rojo) */}
										<Area
											type="monotone"
											dataKey="desfavorable"
											stroke="#EF4444"
											strokeWidth={1.5}
											strokeDasharray="4 3"
											fill="none"
											dot={false}
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>

							<div className="-mt-2 space-y-1 px-1">
								<p className="text-xs text-muted-foreground flex items-center gap-1">
									<Info className="w-3 h-3 shrink-0" />
									{translate('simulator.pension.simulationNote')
										.replace(
											'{period}',
											periodWithRange(
												translate,
												planReturn.periodYears,
												periodRangeLabel(translate, planReturn.period)
											)
										)
										.replace(
											'{returnPct}',
											todayPct(translate, planReturn, `${fmtNum(planReturn.annualPct)} %`)
										)}
								</p>
								<p className="text-xs text-muted-foreground">
									{translate('simulator.pension.disclaimer')}
								</p>
							</div>
						</div>
					</motion.div>
				</>
			)}
		</motion.div>
	);
};

export default PensionSimulatorV2;
